import PropTypes from 'prop-types'
import React, {useRef} from 'react'

import {
    AspectRatio,
    Box,
    Button,
    Flex,
    Image,
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    Text,
    useDisclosure,
    useMediaQuery,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {colors, styleConfigChakraKeys} from '../../constants'
import {LeftChevronIcon, RightChevronIcon} from '../icons'
import Link from '../link'
import {FormattedMessage} from 'react-intl'
import _ from 'lodash'
import {contentfulMediaFormatter} from '../../utils'

function ChildModal(props) {
    const {items, item, media, name, isNested, ivalue, closeDrawer, ...rest} =
        props
    const btnRef = useRef()
    const {isOpen, onOpen, onClose} = useDisclosure()
    const styles = useMultiStyleConfig(styleConfigChakraKeys.drawerMenu)

    return (
        <Box>
            {/* Heading */}
            <Box>
                {items?.length > 0 ? (
                    <Button
                        ref={btnRef}
                        onClick={() => {
                            if (item?.isDisabled) return
                            onOpen()
                        }}
                        {...styles.mobileNavLink}
                        marginLeft={isNested ? 'unset' : 'auto'}
                        textAlign={isNested ? 'left' : 'right'}
                        maxWidth="unset"
                        style={{'--index': ivalue}}
                        sx={{'-webkit-tap-highlight-color': 'transparent'}}
                        {...(item?.isDisabled ? {pointerEvents: 'none'} : {})}
                        color={item?.isDisabled ? colors.grayish : colors.black}
                    >
                        <Text paddingRight="2px">{name}</Text>
                        {!item?.isDisabled && (
                            <RightChevronIcon
                                {...styles.chevronIcon}
                                color="#000000"
                            />
                        )}
                    </Button>
                ) : (
                    <Link
                        {..._.omit(item, [
                            'contentType',
                            'publishedVersion',
                            'isNested'
                        ])}
                        {...styles.mobileNavLink}
                        marginLeft={isNested ? 'unset' : 'auto'}
                        textAlign={isNested ? 'left' : 'right'}
                        style={{
                            '--index': ivalue,
                            color: item?.isDisabled
                                ? colors.grayish
                                : '#000000',
                            backgroundColor: item?.theme?.backgroundColor,
                            background: item?.theme?.backgroundColor,
                            padding: '8px 25px',
                            _hover: {
                                backgroundColor: item?.theme?.backgroundColor,
                                background: item?.theme?.backgroundColor
                            }
                        }}
                        {...(item?.isDisabled ? {pointerEvents: 'none'} : {})}
                        onLinkClick={() => {
                            if (item?.isDisabled) return
                            closeDrawer()
                        }}
                        onClick={() => {
                            if (item?.isDisabled) return
                            closeDrawer()
                        }}
                        sx={{'-webkit-tap-highlight-color': 'transparent'}}
                    />
                )}
            </Box>

            {(items?.length > 0 || media?.url) && (
                <Modal
                    isOpen={isOpen}
                    onClose={onClose}
                    finalFocusRef={btnRef}
                    size={'full'}
                    variant={'megaNavModal'}
                >
                    <ModalContent {...styles.modalContent}>
                        <ModalBody display="flex" {...styles.modalBody}>
                            <Box marginLeft="auto" marginRight="auto">
                                <Button
                                    {...styles.mobileNavLink}
                                    paddingLeft="0"
                                    fontWeight="500"
                                    marginLeft="unset"
                                    onClick={onClose}
                                    style={{'--index': -1}}
                                    sx={{
                                        '-webkit-tap-highlight-color':
                                            'transparent'
                                    }}
                                >
                                    <LeftChevronIcon
                                        {...styles.chevronIcon}
                                        marginRight="4px"
                                    />{' '}
                                    <Text>
                                        <FormattedMessage
                                            defaultMessage="Back"
                                            id="drawer_menu.button.back"
                                        />
                                    </Text>
                                </Button>
                                <NestedMobileMenu
                                    {...rest}
                                    items={items}
                                    isNested={true}
                                    isNavOpen={isOpen}
                                    closeDrawer={closeDrawer}
                                />
                            </Box>
                        </ModalBody>
                        <ModalFooter paddingBottom="41px !important">
                            {media?.url && (
                                <Flex
                                    width="100%"
                                    alignItems="center"
                                    justifyContent="flex-end"
                                    overflow="hidden"
                                >
                                    <AspectRatio
                                        ratio={2 / 3}
                                        width="100%"
                                        maxHeight="330px"
                                        height={{base: '40vh', md: '100%'}}
                                    >
                                        <Image
                                            width="100%"
                                            borderRadius="24px"
                                            data-testid={'footer-banner'}
                                            src={media?.url}
                                            ignoreFallback={true}
                                            alt={media?.url}
                                        />
                                    </AspectRatio>
                                </Flex>
                            )}
                        </ModalFooter>
                    </ModalContent>
                </Modal>
            )}
        </Box>
    )
}

ChildModal.propTypes = {
    media: PropTypes.object,
    items: PropTypes.array,
    item: PropTypes.object,
    name: PropTypes.string,
    isNested: PropTypes.bool,
    isNavOpen: PropTypes.bool,
    ivalue: PropTypes.number,
    closeDrawer: PropTypes.func
}

function NestedMobileMenu(props) {
    const {isNested = false, isNavOpen, items, closeDrawer} = props
    const [isDesktop] = useMediaQuery('(min-width: 992px)')

    return (
        <>
            {items.map((item, iV) => {
                const items = item?.items || []
                const name = item?.heading || item?.text || ''
                const subItems = items?.map((itm) => ({
                    ...itm.sys,
                    ...itm.fields
                }))
                const assets = contentfulMediaFormatter(item?.media)
                const asset =
                    !isDesktop && assets?.mobile?.url
                        ? assets?.mobile
                        : assets?.desktop

                return (
                    <ChildModal
                        media={asset}
                        items={subItems}
                        name={name}
                        item={item}
                        isNested={isNested}
                        isNavOpen={isNavOpen}
                        ivalue={iV}
                        key={item?.sys?.id}
                        closeDrawer={closeDrawer}
                    />
                )
            })}
        </>
    )
}

NestedMobileMenu.propTypes = {
    isNested: PropTypes.bool,
    isNavOpen: PropTypes.bool,
    items: PropTypes.array,
    closeDrawer: PropTypes.func
}

export default NestedMobileMenu
